import { TestBed } from '@angular/core/testing';
import { Router } from '@angular/router';
import type { Company, User } from '@indutrust/shared-model';
import { createMockCompany, MOCK_USER } from '@indutrust/shared-model';
import type { WritableStateSource } from '@ngrx/signals';
import { patchState } from '@ngrx/signals';
import { vi } from 'vitest';

/**
 * Simple helper to prevent navigation during tests by mocking router.navigate
 * Call this in your test beforeEach to avoid redirect errors during AuthSessionStore initialization
 *
 * @example
 * beforeEach(() => {
 *   skipLogoutNavigation();
 *   // ... rest of test setup
 * });
 */
export function skipLogoutNavigation(): void {
  const router = TestBed.inject(Router);
  vi.spyOn(router, 'navigate').mockResolvedValue(true);
}

/**
 * Default mock {@link Company} used when wiring an authenticated session
 * in tests. Built from the canonical fixture in `@indutrust/shared-model`
 * so MSW handlers and unit tests stay in sync.
 */
export const DEFAULT_MOCK_COMPANY: Company = createMockCompany({
  name: 'Test Company',
});

/**
 * Sets up authenticated state in the AuthSessionStore for testing purposes.
 * This bypasses the login flow and allows components to load as if the user is already authenticated.
 *
 * **Usage in your tests:**
 *
 * ```typescript
 * import { setupAuthenticatedSession } from '@indutrust/shared-util';
 * import { AuthSessionStore } from '@indutrust/auth-data';
 *
 * beforeEach(async () => {
 *   await TestBed.configureTestingModule({
 *     imports: [MyComponent, getTranslocoTestingModule()],
 *     providers: [provideRouter(routes)],
 *   }).compileComponents();
 *
 *   const authStore = TestBed.inject(AuthSessionStore);
 *   setupAuthenticatedSession(authStore);
 *
 *   fixture = TestBed.createComponent(MyComponent);
 *   fixture.detectChanges();
 * });
 * ```
 *
 * @param authStore - The AuthSessionStore instance to configure
 * @param user - Optional mock user. Defaults to {@link MOCK_USER} (admin)
 * @param company - Optional mock company data
 * @example
 * // Setup with default admin user
 * setupAuthenticatedSession(authStore);
 *
 * // Setup with a supplier user
 * setupAuthenticatedSession(authStore, createMockUser({ type: 'supplier' }));
 *
 * // Setup with custom user and company
 * setupAuthenticatedSession(
 *   authStore,
 *   createMockUser({ type: 'buyer' }),
 *   { id: 42, name: 'Custom Company' }
 * );
 */
export function setupAuthenticatedSession(
  authStore: WritableStateSource<object>,
  user?: User | null,
  company?: Company | null
): void {
  const mockUser = user ?? MOCK_USER;
  const mockCompany = company ?? DEFAULT_MOCK_COMPANY;

  patchState(authStore, {
    authenticationInitialized: true,
    accessToken: 'mock-access-token',
    refreshToken: 'mock-refresh-token',
    user: mockUser,
    userRequestState: 'success',
    company: mockCompany,
    companyRequestState: 'success',
  });
}

/**
 * Sets up an unauthenticated session for testing logout or redirect flows.
 *
 * @param authStore - The AuthSessionStore instance to configure
 * @example
 * // Test that unauthenticated users are redirected to login
 * setupUnauthenticatedSession(authStore);
 * // Component should now redirect to /login
 */
export function setupUnauthenticatedSession(
  authStore: WritableStateSource<object>
): void {
  patchState(authStore, {
    authenticationInitialized: true,
    accessToken: null,
    refreshToken: null,
    user: null,
    userRequestState: 'initial',
    company: null,
    companyRequestState: 'initial',
  });
}
